import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Card, CardBody, CardHeader, Code,
  Flex, FormControl, FormHelperText, FormLabel, Heading, HStack, IconButton,
  Image, Input, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Select, SimpleGrid, Switch, Tab,
  Table, TabList, TabPanel, TabPanels, Tabs, Tbody, Td, Text, Textarea, Th,
  Thead, Tr, useDisclosure, useToast, VStack,
} from '@chakra-ui/react';
import {
  FiArrowDown, FiArrowUp, FiCopy, FiDownload, FiEdit2, FiExternalLink,
  FiPlus, FiTrash2,
} from 'react-icons/fi';
import { Trans, useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import PageShell from '../components/PageShell';
import ConfirmModal from '../components/ConfirmModal';
import guestAppService from '../services/guestAppService';

// Origin of the backend (logoUrl comes back as a /api/... relative path)
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api')
  .replace(/\/api\/?$/, '');

// Icon slugs the guest app knows how to render next to sections
const SECTION_ICONS = [
  'wifi', 'clock', 'coffee', 'key', 'car', 'map-pin', 'phone', 'info',
  'utensils', 'moon', 'sparkles', 'baby', 'concierge-bell', 'log-out',
];

// Values are i18n keys — translated where rendered
const POLICY_LABELS = {
  pay_at_checkout: 'guestApp.policy.payAtCheckout',
  full_before_checkin: 'guestApp.policy.fullBeforeCheckin',
  deposit_before_checkin: 'guestApp.policy.depositBeforeCheckin',
};

const emptySectionForm = (languages) => ({
  slug: '',
  icon: 'info',
  group: 'stay',
  isVisible: true,
  translations: Object.fromEntries(
    (languages || ['en']).map((lang) => [lang, { title: '', body: '' }])
  ),
});

export default function GuestApp() {
  const { t } = useTranslation();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [sections, setSections] = useState([]);
  const [groups, setGroups] = useState([]);
  const [languages, setLanguages] = useState(['en']);
  const [qrDataUrl, setQrDataUrl] = useState('');
  // Reception desk QR codes — one per flow ({}: kind -> data URL)
  const [flowQrs, setFlowQrs] = useState({});

  // Section editor modal state
  const editor = useDisclosure();
  const [sectionForm, setSectionForm] = useState(null);
  const [editingSlug, setEditingSlug] = useState(null); // null = creating
  const [sectionSaving, setSectionSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const logoInputRef = useRef(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const removeLogo = useDisclosure();

  const seedForm = (data) => ({
    features: { ...data.features },
    housekeepingShowWhen: data.housekeepingShowWhen !== false,
    // Opt-in (default off), unlike the !== false opt-out toggles above
    autoSendGuestLink: data.autoSendGuestLink === true,
    notificationsEnabled: data.notificationsEnabled !== false,
    welcomeText: data.welcomeText || '',
    checkoutInstructions: data.checkoutInstructions || '',
    checkoutVerification: data.checkoutVerification || 'booking_code',
    checkoutVerificationKey: data.checkoutVerificationKey || '',
    buttonLabels: { ...(data.buttonLabels || {}) },
    notificationEmail: data.notificationEmail || '',
    helpContactMode: data.helpContactMode || 'phone',
    helpWhatsapp: data.helpWhatsapp || '',
    brandColor: data.brandColor || '',
    headerColor: data.headerColor || '',
    description: data.description || '',
    checkinPaymentPolicy: data.checkinPaymentPolicy || 'pay_at_checkout',
    checkinDepositType: data.checkinDepositType || 'percent',
    checkinDepositValue: data.checkinDepositValue ?? 0,
    checkinKioskPayAtReception: data.checkinKioskPayAtReception !== false,
  });

  const load = async () => {
    try {
      setLoading(true);
      const [cfg, secs] = await Promise.all([
        guestAppService.getConfig(),
        guestAppService.getSections(),
      ]);
      setConfig(cfg);
      setForm(seedForm(cfg));
      setSections(secs.sections || []);
      setGroups(secs.groups || []);
      setLanguages(secs.supportedLanguages || ['en']);
      setError('');
    } catch {
      setError(t('guestApp.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (config?.guestAppUrl) {
      QRCode.toDataURL(config.guestAppUrl, { width: 440, margin: 1 })
        .then(setQrDataUrl)
        .catch(() => setQrDataUrl(''));
    }
  }, [config?.guestAppUrl]);

  useEffect(() => {
    const targets = {
      checkin: config?.guestAppCheckinUrl,
      checkout: config?.guestAppCheckoutUrl,
    };
    Promise.all(
      Object.entries(targets)
        .filter(([, url]) => !!url)
        .map(([kind, url]) =>
          QRCode.toDataURL(url, { width: 440, margin: 1 })
            .then((dataUrl) => [kind, dataUrl])
            .catch(() => [kind, ''])
        )
    ).then((pairs) => setFlowQrs(Object.fromEntries(pairs)));
  }, [config?.guestAppCheckinUrl, config?.guestAppCheckoutUrl]);

  const setF = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const setFeature = (key) => (e) =>
    setForm((p) => ({ ...p, features: { ...p.features, [key]: e.target.checked } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        checkinDepositValue: Number(form.checkinDepositValue) || 0,
      };
      const res = await guestAppService.updateConfig(payload);
      setConfig(res);
      setForm(seedForm(res));
      toast({ title: t('guestApp.toasts.settingsSaved'), status: 'success', duration: 2500 });
    } catch (err) {
      const body = err.response?.data;
      const detail = body?.errors
        ? Object.values(body.errors).join(' ')
        : body?.detail || body?.message || t('guestApp.toasts.checkFieldsRetry');
      toast({ title: t('guestApp.toasts.saveFailed'), description: detail,
              status: 'error', duration: 9000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const copy = (value, label) => {
    navigator.clipboard.writeText(value);
    toast({ title: t('guestApp.toasts.copied', { label }), status: 'success', duration: 1500 });
  };

  // ---------------------------------------------------------- sections
  const openCreate = () => {
    setEditingSlug(null);
    setSectionForm(emptySectionForm(languages));
    editor.onOpen();
  };

  const openEdit = (section) => {
    setEditingSlug(section.slug);
    setSectionForm({
      slug: section.slug,
      icon: section.icon || 'info',
      group: section.group || 'stay',
      isVisible: section.isVisible !== false,
      sortOrder: section.sortOrder,
      translations: Object.fromEntries(
        languages.map((lang) => [
          lang,
          {
            title: section.translations?.[lang]?.title || '',
            body: section.translations?.[lang]?.body || '',
          },
        ])
      ),
    });
    editor.onOpen();
  };

  const saveSection = async () => {
    setSectionSaving(true);
    try {
      if (editingSlug) {
        await guestAppService.updateSection(editingSlug, sectionForm);
      } else {
        await guestAppService.createSection(sectionForm);
      }
      toast({ title: editingSlug ? t('guestApp.toasts.sectionUpdated') : t('guestApp.toasts.sectionAdded'),
              status: 'success', duration: 2000 });
      editor.onClose();
      const secs = await guestAppService.getSections();
      setSections(secs.sections || []);
    } catch (err) {
      const body = err.response?.data;
      toast({ title: t('guestApp.toasts.sectionSaveFailed'),
              description: body?.message || body?.detail || t('guestApp.toasts.checkFields'),
              status: 'error', duration: 8000, isClosable: true });
    } finally {
      setSectionSaving(false);
    }
  };

  const toggleSectionVisible = async (section) => {
    try {
      await guestAppService.updateSection(section.slug, {
        icon: section.icon, group: section.group, sortOrder: section.sortOrder,
        isVisible: !(section.isVisible !== false),
        translations: section.translations,
      });
      const secs = await guestAppService.getSections();
      setSections(secs.sections || []);
    } catch {
      toast({ title: t('guestApp.toasts.visibilityFailed'), status: 'error', duration: 4000 });
    }
  };

  const moveSection = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= sections.length) return;
    const order = sections.map((s) => s.slug);
    [order[index], order[target]] = [order[target], order[index]];
    try {
      await guestAppService.reorderSections(order);
      const secs = await guestAppService.getSections();
      setSections(secs.sections || []);
    } catch {
      toast({ title: t('guestApp.toasts.reorderFailed'), status: 'error', duration: 4000 });
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await guestAppService.deleteSection(deleteTarget.slug);
      toast({ title: t('guestApp.toasts.sectionDeleted'), status: 'success', duration: 2000 });
      setDeleteTarget(null);
      const secs = await guestAppService.getSections();
      setSections(secs.sections || []);
    } catch {
      toast({ title: t('guestApp.toasts.sectionDeleteFailed'), status: 'error', duration: 4000 });
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------- branding
  const handleLogoFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setLogoBusy(true);
    try {
      const res = await guestAppService.uploadLogo(file);
      setConfig((p) => ({ ...p, logoUrl: res.logoUrl }));
      toast({ title: t('guestApp.toasts.logoUploaded'), status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: t('guestApp.toasts.logoUploadFailed'),
              description: err.response?.data?.message || t('guestApp.toasts.logoFormatHint'),
              status: 'error', duration: 8000, isClosable: true });
    } finally {
      setLogoBusy(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoBusy(true);
    try {
      await guestAppService.deleteLogo();
      setConfig((p) => ({ ...p, logoUrl: null }));
      removeLogo.onClose();
      toast({ title: t('guestApp.toasts.logoRemoved'), status: 'success', duration: 2000 });
    } catch {
      toast({ title: t('guestApp.toasts.logoRemoveFailed'), status: 'error', duration: 4000 });
    } finally {
      setLogoBusy(false);
    }
  };

  const featureRow = (key, label, hint) => (
    <FormControl display="flex" alignItems="flex-start" key={key}>
      <Switch size="sm" mt={1} mr={3}
        isChecked={!!form.features[key]}
        onChange={setFeature(key)} />
      <Box>
        <FormLabel fontSize="sm" mb={0}>{label}</FormLabel>
        <Text fontSize="xs" color="gray.500">{hint}</Text>
      </Box>
    </FormControl>
  );

  return (
    <PageShell
      title={t('guestApp.page.title')}
      subtitle={t('guestApp.page.subtitle')}
      breadcrumbs={[{ label: t('guestApp.page.title') }]}
      loading={loading}
      error={error}
      actions={
        <Button size="sm" colorScheme="blue" onClick={handleSave} isLoading={saving}>
          {t('guestApp.buttons.saveSettings')}
        </Button>
      }
    >
      {form && config && (
        <Tabs size="sm" variant="enclosed">
          <TabList>
            <Tab>{t('guestApp.tabs.share')}</Tab>
            <Tab>{t('guestApp.tabs.buttons')}</Tab>
            <Tab>{t('guestApp.tabs.checkinWorkflow')}</Tab>
            <Tab>{t('guestApp.tabs.branding')}</Tab>
            <Tab>{t('guestApp.tabs.content', { n: sections.length })}</Tab>
          </TabList>
          <TabPanels>
            {/* ------------------------------------------------ Share & QR */}
            <TabPanel px={0}>
              <VStack align="stretch" spacing={6}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Card>
                  <CardHeader pb={2}><Heading size="sm">{t('guestApp.share.guestLinkTitle')}</Heading></CardHeader>
                  <CardBody pt={0}>
                    <VStack align="stretch" spacing={4}>
                      <Text fontSize="sm" color="gray.600">
                        {t('guestApp.share.guestLinkIntro')}
                      </Text>
                      <FormControl>
                        <FormLabel fontSize="sm">{t('guestApp.share.shareLink')}</FormLabel>
                        <HStack>
                          <Code fontSize="xs" p={2} flex="1" overflowX="auto">
                            {config.guestAppUrl}
                          </Code>
                          <IconButton size="sm" aria-label={t('guestApp.share.copyLink')} icon={<FiCopy />}
                            onClick={() => copy(config.guestAppUrl, t('guestApp.share.linkLabel'))} />
                          <IconButton size="sm" aria-label={t('guestApp.share.open')} icon={<FiExternalLink />}
                            as="a" href={config.guestAppUrl} target="_blank" rel="noreferrer" />
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">{t('guestApp.share.kioskLink')}</FormLabel>
                        <HStack>
                          <Code fontSize="xs" p={2} flex="1" overflowX="auto">
                            {config.guestAppKioskUrl}
                          </Code>
                          <IconButton size="sm" aria-label={t('guestApp.share.copyKioskLink')} icon={<FiCopy />}
                            onClick={() => copy(config.guestAppKioskUrl, t('guestApp.share.kioskLinkLabel'))} />
                        </HStack>
                        <FormHelperText fontSize="xs">
                          {t('guestApp.share.kioskHelp')}
                        </FormHelperText>
                      </FormControl>
                    </VStack>
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader pb={2}><Heading size="sm">{t('guestApp.share.qrTitle')}</Heading></CardHeader>
                  <CardBody pt={0}>
                    <VStack spacing={3}>
                      {qrDataUrl ? (
                        <Image src={qrDataUrl} alt={t('guestApp.share.qrAlt')} boxSize="220px"
                          borderWidth="1px" borderRadius="md" p={2} bg="white" />
                      ) : (
                        <Text fontSize="sm" color="gray.500">{t('guestApp.share.generating')}</Text>
                      )}
                      <Button size="sm" leftIcon={<FiDownload />} as="a"
                        href={qrDataUrl} download={`guest-app-${config.hotelCode}.png`}
                        isDisabled={!qrDataUrl}>
                        {t('guestApp.share.downloadPng')}
                      </Button>
                      <Text fontSize="xs" color="gray.500" textAlign="center">
                        {t('guestApp.share.qrPrintHint')}
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              </SimpleGrid>

              {/* Reception desk QR codes — one per flow */}
              <Card>
                <CardHeader pb={2}>
                  <Heading size="sm">{t('guestApp.share.receptionTitle')}</Heading>
                </CardHeader>
                <CardBody pt={0}>
                  <Text fontSize="sm" color="gray.600" mb={4}>
                    {t('guestApp.share.receptionIntro')}
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {[
                      {
                        kind: 'checkin',
                        title: t('guestApp.share.checkinQr'),
                        url: config.guestAppCheckinUrl,
                        enabled: form.features.selfCheckin,
                        offNote: t('guestApp.share.checkinQrOff'),
                        hint: t('guestApp.share.checkinQrHint'),
                      },
                      {
                        kind: 'checkout',
                        title: t('guestApp.share.checkoutQr'),
                        url: config.guestAppCheckoutUrl,
                        enabled: form.features.selfCheckout,
                        offNote: t('guestApp.share.checkoutQrOff'),
                        hint: t('guestApp.share.checkoutQrHint'),
                      },
                    ].map(({ kind, title, url, enabled, offNote, hint }) => (
                      <Box key={kind} borderWidth="1px" borderRadius="md" p={4}>
                        <HStack justify="space-between" mb={3}>
                          <Heading size="xs">{title}</Heading>
                          <Badge colorScheme={enabled ? 'green' : 'orange'} fontSize="2xs">
                            {enabled ? t('guestApp.share.active') : t('guestApp.share.featureOff')}
                          </Badge>
                        </HStack>
                        <VStack spacing={3}>
                          {flowQrs[kind] ? (
                            <Image src={flowQrs[kind]} alt={title} boxSize="190px"
                              borderWidth="1px" borderRadius="md" p={2} bg="white" />
                          ) : (
                            <Text fontSize="sm" color="gray.500">{t('guestApp.share.generating')}</Text>
                          )}
                          <HStack w="100%">
                            <Code fontSize="xs" p={2} flex="1" overflowX="auto">{url}</Code>
                            <IconButton size="sm" aria-label={t('guestApp.share.copyItem', { title })} icon={<FiCopy />}
                              onClick={() => copy(url, title)} />
                          </HStack>
                          <Button size="sm" leftIcon={<FiDownload />} as="a" w="100%"
                            href={flowQrs[kind]}
                            download={`${kind}-qr-${config.hotelCode}.png`}
                            isDisabled={!flowQrs[kind]}>
                            {t('guestApp.share.downloadPng')}
                          </Button>
                          <Text fontSize="xs" color={enabled ? 'gray.500' : 'orange.600'} textAlign="center">
                            {enabled ? hint : offNote}
                          </Text>
                        </VStack>
                      </Box>
                    ))}
                  </SimpleGrid>
                </CardBody>
              </Card>
              </VStack>
            </TabPanel>

            {/* ------------------------------------------ Buttons & Features */}
            <TabPanel px={0}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Card>
                  <CardHeader pb={2}><Heading size="sm">{t('guestApp.features.appButtonsTitle')}</Heading></CardHeader>
                  <CardBody pt={0}>
                    <VStack align="stretch" spacing={4}>
                      {featureRow('selfCheckin', t('guestApp.features.selfCheckin'),
                        t('guestApp.features.selfCheckinHint'))}
                      {featureRow('selfCheckout', t('guestApp.features.selfCheckout'),
                        t('guestApp.features.selfCheckoutHint'))}
                      {form.features.selfCheckout && (
                        <FormControl pl={8}>
                          <FormLabel fontSize="sm" mb={1}>{t('guestApp.features.checkoutInstructions')}</FormLabel>
                          <Textarea size="sm" rows={4}
                            value={form.checkoutInstructions}
                            placeholder={t('guestApp.features.checkoutInstructionsPlaceholder')}
                            onChange={(e) => setForm((p) => ({ ...p, checkoutInstructions: e.target.value }))} />
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {t('guestApp.features.checkoutInstructionsHelp')}
                          </Text>

                          <FormLabel fontSize="sm" mt={4} mb={1}>{t('guestApp.features.verificationLabel')}</FormLabel>
                          <Select size="sm"
                            value={form.checkoutVerification}
                            onChange={(e) => setForm((p) => ({ ...p, checkoutVerification: e.target.value }))}>
                            <option value="booking_code">{t('guestApp.features.verifyBookingCode')}</option>
                            <option value="room_number">{t('guestApp.features.verifyRoomNumber')}</option>
                            <option value="room_number_custom">{t('guestApp.features.verifyRoomNumberCustom')}</option>
                          </Select>
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {t('guestApp.features.verificationHelp')}
                          </Text>

                          {form.checkoutVerification === 'room_number_custom' && (
                            <Box mt={3}>
                              <FormLabel fontSize="sm" mb={1}>{t('guestApp.features.secondFactorLabel')}</FormLabel>
                              {(config.roomCustomKeys || []).length > 0 ? (
                                <>
                                  <Select size="sm"
                                    placeholder={t('guestApp.features.secondFactorPlaceholder')}
                                    value={form.checkoutVerificationKey}
                                    onChange={(e) => setForm((p) => ({ ...p, checkoutVerificationKey: e.target.value }))}>
                                    {(config.roomCustomKeys || []).map((key) => (
                                      <option key={key} value={key}>{`{{${key}}}`}</option>
                                    ))}
                                  </Select>
                                  <Text fontSize="xs" color="gray.500" mt={1}>
                                    {t('guestApp.features.secondFactorHelp')}
                                  </Text>
                                  {(() => {
                                    const cov = config.checkoutVerificationCoverage;
                                    if (!cov || cov.key !== form.checkoutVerificationKey) return null;
                                    if (cov.withValue === 0) {
                                      return (
                                        <Alert status="error" borderRadius="md" fontSize="xs" mt={2}>
                                          <AlertIcon />
                                          <Trans
                                            i18nKey="guestApp.features.coverageNone"
                                            values={{ token: `{{${cov.key}}}` }}
                                            components={{ b: <b /> }}
                                          />
                                        </Alert>
                                      );
                                    }
                                    if (cov.withValue < cov.totalRooms) {
                                      return (
                                        <Alert status="warning" borderRadius="md" fontSize="xs" mt={2}>
                                          <AlertIcon />
                                          {t('guestApp.features.coveragePartial', {
                                            withValue: cov.withValue, total: cov.totalRooms,
                                          })}
                                        </Alert>
                                      );
                                    }
                                    return (
                                      <Text fontSize="xs" color="green.600" mt={2}>
                                        {t('guestApp.features.coverageAll', { total: cov.totalRooms })}
                                      </Text>
                                    );
                                  })()}
                                </>
                              ) : (
                                <Alert status="warning" borderRadius="md" fontSize="xs">
                                  <AlertIcon />
                                  {t('guestApp.features.noCustomValues')}
                                </Alert>
                              )}
                            </Box>
                          )}
                        </FormControl>
                      )}
                      {featureRow('foodOrdering', t('guestApp.features.orderFood'),
                        t('guestApp.features.orderFoodHint'))}
                      {featureRow('housekeeping', t('guestApp.features.housekeeping'),
                        t('guestApp.features.housekeepingHint'))}
                      {form.features.housekeeping && (
                        <FormControl display="flex" alignItems="flex-start" pl={8}>
                          <Switch size="sm" mt={1} mr={3}
                            isChecked={form.housekeepingShowWhen}
                            onChange={(e) => setForm((p) => (
                              { ...p, housekeepingShowWhen: e.target.checked }
                            ))} />
                          <Box>
                            <FormLabel fontSize="sm" mb={0}>{t('guestApp.features.showWhen')}</FormLabel>
                            <Text fontSize="xs" color="gray.500">
                              {t('guestApp.features.showWhenHint')}
                            </Text>
                          </Box>
                        </FormControl>
                      )}
                      {featureRow('payments', t('guestApp.features.payInAdvance'),
                        t('guestApp.features.payInAdvanceHint'))}
                      <Text fontSize="xs" color="gray.500">
                        {t('guestApp.features.hotelInfoAlwaysShown')}
                      </Text>
                      <Box borderTopWidth="1px" pt={4}>
                        <Text fontSize="sm" fontWeight="600" mb={1}>{t('guestApp.buttonNames.title')}</Text>
                        <Text fontSize="xs" color="gray.500" mb={3}>
                          {t('guestApp.buttonNames.help')}
                        </Text>
                        <VStack align="stretch" spacing={2}>
                          {[
                            ['checkin', t('guestApp.buttonNames.checkin')],
                            ['food', t('guestApp.buttonNames.food')],
                            ['housekeeping', t('guestApp.buttonNames.housekeeping')],
                            ['pay', t('guestApp.buttonNames.pay')],
                            ['info', t('guestApp.buttonNames.info')],
                          ].map(([key, fallback]) => (
                            <HStack key={key}>
                              <Text fontSize="xs" color="gray.500" w="130px" flexShrink={0}>
                                {fallback}
                              </Text>
                              <Input size="sm" maxLength={40} placeholder={fallback}
                                value={form.buttonLabels[key] || ''}
                                onChange={(e) => setForm((p) => ({
                                  ...p,
                                  buttonLabels: { ...p.buttonLabels, [key]: e.target.value },
                                }))} />
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader pb={2}><Heading size="sm">{t('guestApp.help.title')}</Heading></CardHeader>
                  <CardBody pt={0}>
                    <VStack align="stretch" spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">{t('guestApp.help.contactMethod')}</FormLabel>
                        <Select size="sm" value={form.helpContactMode} onChange={setF('helpContactMode')}>
                          <option value="phone">{t('guestApp.help.modePhone')}</option>
                          <option value="whatsapp">{t('guestApp.help.modeWhatsapp')}</option>
                          <option value="hidden">{t('guestApp.help.modeHidden')}</option>
                        </Select>
                      </FormControl>
                      {form.helpContactMode === 'whatsapp' && (
                        <FormControl>
                          <FormLabel fontSize="sm">{t('guestApp.help.whatsappNumber')}</FormLabel>
                          <Input size="sm" placeholder="+919876543210"
                            value={form.helpWhatsapp} onChange={setF('helpWhatsapp')} />
                          <FormHelperText fontSize="xs">
                            {t('guestApp.help.whatsappHelp')}
                          </FormHelperText>
                        </FormControl>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
                <Card gridColumn={{ md: '1 / -1' }}>
                  <CardHeader pb={2}><Heading size="sm">{t('guestApp.emails.title')}</Heading></CardHeader>
                  <CardBody pt={0}>
                    <FormControl display="flex" alignItems="flex-start">
                      <Switch size="sm" mt={1} mr={3}
                        isChecked={form.autoSendGuestLink}
                        onChange={(e) => setForm((p) => (
                          { ...p, autoSendGuestLink: e.target.checked }
                        ))} />
                      <Box>
                        <FormLabel fontSize="sm" mb={0}>
                          {t('guestApp.emails.autoSend')}
                        </FormLabel>
                        <Text fontSize="xs" color="gray.500">
                          {t('guestApp.emails.autoSendHelp')}
                        </Text>
                      </Box>
                    </FormControl>
                  </CardBody>
                </Card>
                <Card gridColumn={{ md: '1 / -1' }}>
                  <CardHeader pb={2}><Heading size="sm">{t('guestApp.notifications.title')}</Heading></CardHeader>
                  <CardBody pt={0}>
                    <VStack align="stretch" spacing={4}>
                      <FormControl display="flex" alignItems="flex-start">
                        <Switch size="sm" mt={1} mr={3}
                          isChecked={form.notificationsEnabled}
                          onChange={(e) => setForm((p) => (
                            { ...p, notificationsEnabled: e.target.checked }
                          ))} />
                        <Box>
                          <FormLabel fontSize="sm" mb={0}>
                            {t('guestApp.notifications.enable')}
                          </FormLabel>
                          <Text fontSize="xs" color="gray.500">
                            {t('guestApp.notifications.enableHelp')}
                          </Text>
                        </Box>
                      </FormControl>
                      {form.notificationsEnabled && (
                        <FormControl maxW="480px">
                          <FormLabel fontSize="sm">{t('guestApp.notifications.notifierEmail')}</FormLabel>
                          <Input size="sm" placeholder="frontdesk@yourhotel.com, kitchen@yourhotel.com"
                            value={form.notificationEmail} onChange={setF('notificationEmail')} />
                          <FormHelperText fontSize="xs">
                            {config.hotelNotificationEmails?.length
                              ? t('guestApp.notifications.notifierHelpWithEmails', {
                                emails: config.hotelNotificationEmails.join(', '),
                              })
                              : t('guestApp.notifications.notifierHelpNoEmails')}
                          </FormHelperText>
                        </FormControl>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              </SimpleGrid>
            </TabPanel>

            {/* -------------------------------------------- Check-in workflow */}
            <TabPanel px={0}>
              <Card maxW="640px">
                <CardHeader pb={2}><Heading size="sm">{t('guestApp.checkin.title')}</Heading></CardHeader>
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">{t('guestApp.checkin.policy')}</FormLabel>
                      <Select size="sm" value={form.checkinPaymentPolicy}
                        onChange={setF('checkinPaymentPolicy')}>
                        {Object.entries(POLICY_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>{t(label)}</option>
                        ))}
                      </Select>
                      <FormHelperText fontSize="xs">
                        {t('guestApp.checkin.policyHelp')}
                      </FormHelperText>
                    </FormControl>
                    {form.checkinPaymentPolicy === 'deposit_before_checkin' && (
                      <HStack align="flex-end" spacing={4}>
                        <FormControl maxW="240px">
                          <FormLabel fontSize="sm">{t('guestApp.checkin.depositType')}</FormLabel>
                          <Select size="sm" value={form.checkinDepositType}
                            onChange={setF('checkinDepositType')}>
                            <option value="percent">{t('guestApp.checkin.depositPercent')}</option>
                            <option value="fixed">{t('guestApp.checkin.depositFixed')}</option>
                            <option value="first_night">{t('guestApp.checkin.depositFirstNight')}</option>
                          </Select>
                        </FormControl>
                        {form.checkinDepositType !== 'first_night' && (
                          <FormControl maxW="160px">
                            <FormLabel fontSize="sm">
                              {form.checkinDepositType === 'percent' ? t('guestApp.checkin.percentLabel') : t('guestApp.checkin.amountLabel')}
                            </FormLabel>
                            <Input size="sm" type="number" min="0"
                              value={form.checkinDepositValue}
                              onChange={setF('checkinDepositValue')} />
                          </FormControl>
                        )}
                      </HStack>
                    )}
                    <FormControl display="flex" alignItems="flex-start">
                      <Switch size="sm" mt={1} mr={3}
                        isChecked={form.checkinKioskPayAtReception}
                        onChange={(e) => setForm((p) => (
                          { ...p, checkinKioskPayAtReception: e.target.checked }
                        ))} />
                      <Box>
                        <FormLabel fontSize="sm" mb={0}>
                          {t('guestApp.checkin.kioskPayAtReception')}
                        </FormLabel>
                        <Text fontSize="xs" color="gray.500">
                          {t('guestApp.checkin.kioskPayAtReceptionHelp')}
                        </Text>
                      </Box>
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>

            {/* --------------------------------------------------- Branding */}
            <TabPanel px={0}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Card>
                  <CardHeader pb={2}><Heading size="sm">{t('guestApp.branding.identityTitle')}</Heading></CardHeader>
                  <CardBody pt={0}>
                    <VStack align="stretch" spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">{t('guestApp.branding.welcomeMessage')}</FormLabel>
                        <Textarea size="sm" rows={2} value={form.welcomeText}
                          onChange={setF('welcomeText')}
                          placeholder={t('guestApp.branding.welcomeMessagePlaceholder')} />
                        <FormHelperText fontSize="xs">
                          {t('guestApp.branding.welcomeMessageHelp')}
                        </FormHelperText>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">{t('guestApp.branding.description')}</FormLabel>
                        <Textarea size="sm" rows={3} value={form.description}
                          onChange={setF('description')}
                          placeholder={t('guestApp.branding.descriptionPlaceholder')} />
                        <FormHelperText fontSize="xs">
                          {t('guestApp.branding.descriptionHelp')}
                        </FormHelperText>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">{t('guestApp.branding.accentColor')}</FormLabel>
                        <HStack>
                          <Input size="sm" type="color" w="48px" p={0.5}
                            value={form.brandColor || '#1A56DB'}
                            onChange={setF('brandColor')} />
                          <Input size="sm" maxW="120px" placeholder="#1A56DB"
                            value={form.brandColor} onChange={setF('brandColor')} />
                          {form.brandColor && (
                            <Button size="xs" variant="ghost"
                              onClick={() => setForm((p) => ({ ...p, brandColor: '' }))}>
                              {t('guestApp.branding.reset')}
                            </Button>
                          )}
                        </HStack>
                        <FormHelperText fontSize="xs">
                          {t('guestApp.branding.accentColorHelp')}
                        </FormHelperText>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">{t('guestApp.branding.headerColor')}</FormLabel>
                        <HStack>
                          <Input size="sm" type="color" w="48px" p={0.5}
                            value={form.headerColor || '#14213D'}
                            onChange={setF('headerColor')} />
                          <Input size="sm" maxW="120px" placeholder="#14213D"
                            value={form.headerColor} onChange={setF('headerColor')} />
                          {form.headerColor && (
                            <Button size="xs" variant="ghost"
                              onClick={() => setForm((p) => ({ ...p, headerColor: '' }))}>
                              {t('guestApp.branding.reset')}
                            </Button>
                          )}
                        </HStack>
                        <FormHelperText fontSize="xs">
                          {t('guestApp.branding.headerColorHelp')}
                        </FormHelperText>
                      </FormControl>
                    </VStack>
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader pb={2}><Heading size="sm">{t('guestApp.branding.logoTitle')}</Heading></CardHeader>
                  <CardBody pt={0}>
                    <VStack align="stretch" spacing={3}>
                      {config.logoUrl ? (
                        <Image src={`${API_ORIGIN}${config.logoUrl}`} alt={t('guestApp.branding.logoAlt')}
                          boxSize="96px" objectFit="cover" borderRadius="lg" borderWidth="1px" />
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          {t('guestApp.branding.noLogo')}
                        </Text>
                      )}
                      <input ref={logoInputRef} type="file" hidden
                        accept="image/png,image/jpeg,image/webp" onChange={handleLogoFile} />
                      <HStack>
                        <Button size="sm" onClick={() => logoInputRef.current?.click()}
                          isLoading={logoBusy}>
                          {config.logoUrl ? t('guestApp.branding.replaceLogo') : t('guestApp.branding.uploadLogo')}
                        </Button>
                        {config.logoUrl && (
                          <Button size="sm" variant="outline" colorScheme="red"
                            onClick={removeLogo.onOpen}>
                            {t('guestApp.branding.remove')}
                          </Button>
                        )}
                      </HStack>
                      <Text fontSize="xs" color="gray.500">{t('guestApp.branding.logoHint')}</Text>
                    </VStack>
                  </CardBody>
                </Card>
              </SimpleGrid>
            </TabPanel>

            {/* ---------------------------------------------------- Content */}
            <TabPanel px={0}>
              <Card>
                <CardHeader pb={2}>
                  <Flex justify="space-between" align="center">
                    <Box>
                      <Heading size="sm">{t('guestApp.content.title')}</Heading>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {t('guestApp.content.intro')}
                      </Text>
                    </Box>
                    <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate}>
                      {t('guestApp.content.addSection')}
                    </Button>
                  </Flex>
                </CardHeader>
                <CardBody pt={0}>
                  {sections.length === 0 ? (
                    <Alert status="info" fontSize="sm"><AlertIcon />
                      {t('guestApp.content.empty')}
                    </Alert>
                  ) : (
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th w="90px">{t('guestApp.content.colOrder')}</Th><Th>{t('guestApp.content.colSection')}</Th>
                          <Th>{t('guestApp.content.colGroup')}</Th>
                          <Th w="80px">{t('guestApp.content.colVisible')}</Th>
                          <Th w="110px" textAlign="right">{t('guestApp.content.colActions')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {sections.map((section, index) => (
                          <Tr key={section.slug}>
                            <Td>
                              <HStack spacing={1}>
                                <IconButton size="xs" variant="ghost" aria-label={t('guestApp.content.moveUp')}
                                  icon={<FiArrowUp />} isDisabled={index === 0}
                                  onClick={() => moveSection(index, -1)} />
                                <IconButton size="xs" variant="ghost" aria-label={t('guestApp.content.moveDown')}
                                  icon={<FiArrowDown />} isDisabled={index === sections.length - 1}
                                  onClick={() => moveSection(index, 1)} />
                              </HStack>
                            </Td>
                            <Td>
                              <Text fontWeight="600" fontSize="sm">
                                {section.translations?.en?.title || section.slug}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {section.slug}
                                {Object.keys(section.translations || {}).length > 1 &&
                                  ` · ${Object.keys(section.translations).join(', ')}`}
                              </Text>
                            </Td>
                            <Td><Badge fontSize="xs">{section.group}</Badge></Td>
                            <Td>
                              <Switch size="sm" isChecked={section.isVisible !== false}
                                onChange={() => toggleSectionVisible(section)} />
                            </Td>
                            <Td textAlign="right">
                              <IconButton size="xs" variant="ghost" aria-label={t('guestApp.content.edit')}
                                icon={<FiEdit2 />} onClick={() => openEdit(section)} />
                              <IconButton size="xs" variant="ghost" colorScheme="red"
                                aria-label={t('guestApp.content.delete')} icon={<FiTrash2 />}
                                onClick={() => setDeleteTarget(section)} />
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  )}
                </CardBody>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}

      {/* Section add/edit modal */}
      <Modal isOpen={editor.isOpen} onClose={editor.onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">
            {editingSlug
              ? t('guestApp.sectionEditor.editTitle', { slug: editingSlug })
              : t('guestApp.content.addSection')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {sectionForm && (
              <VStack align="stretch" spacing={4}>
                {!editingSlug && (
                  <FormControl>
                    <FormLabel fontSize="sm">{t('guestApp.sectionEditor.keyLabel')}</FormLabel>
                    <Input size="sm" placeholder="wifi"
                      value={sectionForm.slug}
                      onChange={(e) => setSectionForm((p) => ({ ...p, slug: e.target.value }))} />
                    <FormHelperText fontSize="xs">
                      {t('guestApp.sectionEditor.keyHelp')}
                    </FormHelperText>
                  </FormControl>
                )}
                <HStack spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">{t('guestApp.sectionEditor.icon')}</FormLabel>
                    <Select size="sm" value={sectionForm.icon}
                      onChange={(e) => setSectionForm((p) => ({ ...p, icon: e.target.value }))}>
                      {SECTION_ICONS.map((icon) => <option key={icon} value={icon}>{icon}</option>)}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">{t('guestApp.sectionEditor.group')}</FormLabel>
                    <Select size="sm" value={sectionForm.group}
                      onChange={(e) => setSectionForm((p) => ({ ...p, group: e.target.value }))}>
                      {(groups.length ? groups : [
                        { key: 'essentials', label: t('guestApp.sectionEditor.groupEssentials') },
                        { key: 'stay', label: t('guestApp.sectionEditor.groupStay') },
                        { key: 'offers', label: t('guestApp.sectionEditor.groupOffers') },
                        { key: 'other', label: t('guestApp.sectionEditor.groupOther') },
                      ]).map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
                    </Select>
                  </FormControl>
                </HStack>
                {languages.map((lang) => (
                  <Box key={lang} borderWidth="1px" borderRadius="md" p={3}>
                    <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2}>
                      {lang === 'en'
                        ? t('guestApp.sectionEditor.langRequired', { lang: lang.toUpperCase() })
                        : t('guestApp.sectionEditor.langOptional', { lang: lang.toUpperCase() })}
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      <Input size="sm" placeholder={t('guestApp.sectionEditor.titlePlaceholder')}
                        value={sectionForm.translations[lang]?.title || ''}
                        onChange={(e) => setSectionForm((p) => ({
                          ...p,
                          translations: {
                            ...p.translations,
                            [lang]: { ...p.translations[lang], title: e.target.value },
                          },
                        }))} />
                      <Textarea size="sm" rows={3} placeholder={t('guestApp.sectionEditor.bodyPlaceholder')}
                        value={sectionForm.translations[lang]?.body || ''}
                        onChange={(e) => setSectionForm((p) => ({
                          ...p,
                          translations: {
                            ...p.translations,
                            [lang]: { ...p.translations[lang], body: e.target.value },
                          },
                        }))} />
                    </VStack>
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={3} onClick={editor.onClose}>{t('guestApp.buttons.cancel')}</Button>
            <Button size="sm" colorScheme="blue" onClick={saveSection} isLoading={sectionSaving}>
              {editingSlug ? t('guestApp.sectionEditor.saveSection') : t('guestApp.content.addSection')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={t('guestApp.confirm.deleteSectionTitle')}
        message={t('guestApp.confirm.deleteSectionMessage', {
          name: deleteTarget?.translations?.en?.title || deleteTarget?.slug,
        })}
        confirmLabel={t('guestApp.content.delete')}
        colorScheme="red"
        loading={deleting}
      />
      <ConfirmModal
        isOpen={removeLogo.isOpen}
        onClose={removeLogo.onClose}
        onConfirm={handleRemoveLogo}
        title={t('guestApp.confirm.removeLogoTitle')}
        message={t('guestApp.confirm.removeLogoMessage')}
        confirmLabel={t('guestApp.branding.remove')}
        colorScheme="red"
        loading={logoBusy}
      />
    </PageShell>
  );
}
